'use strict';
//***********************************************************************************
//***********************************************************************************
//**** cn_svg_tool_space  : Manipulation of space
//***********************************************************************************
//***********************************************************************************

import { cn_svg_tool_creation } from './cn_svg_tool_creation';
import { cn_pastille } from './cn_pastille';
import { cn_add, cn_clone, cn_dist, cn_mul, cn_normalize, cn_polar, cn_size, cn_sub } from '../utils/cn_utilities';
import { cn_background_map } from '../model/cn_background_map';
import { cn_camera } from './cn_camera';
import { cn_handler_rotation } from './cn_handler_rotation';
import { cn_background_map_selection_input, cn_check_input, cn_list_input, cn_notification_input, cn_number_input, cn_progress_input, cn_question_input, SEVERITY_QUESTION_WARNING } from './cn_inputs';
import { cn_edit_box } from './cn_edit_box';
import { HELPER } from '../utils/cn_wordings';
import { cn_dxf_to_json } from '../utils/cn_dxf_parser';
import { cn_vector_arc, cn_vector_data, cn_vector_layer, cn_vector_path, cn_vector_text } from '../model/cn_vector_data';
import { cn_roof } from '../model/cn_roof';
import { cn_element } from '../model/cn_element';

export class cn_svg_tool_background_map extends cn_svg_tool_creation {
    //***********************************************************************************
    /**
     * Constructor
     * @param map
     */
    constructor(map) {
        super(map);
        this.cn_svg_bm = null;
        this._camera = new cn_camera();

        this._mouse_position = [0, 0];

        this._box_size = [80, 20];

        this._mouseover = -1;
        this._selection = -1;
        this._move_handles = [];
        this._current_camera_scale = 0;

        this._edited_map = null;
        this._rotation_handle = null;
        this._edit_box = null;

        this._grabid = 0;
        this._mouseover_map = null;

        this._width_measure = [[0, 0], [0, 0]];
        this._height_measure = [[0, 0], [0, 0]];
        this._scale_measure = [[0, 0], [0, 0]];
        this._vector_measure = [[0, 0], [0, 0]];
        this._selected_vector_element = null;

        this._pending_changes = false;

        this.maptypes = [
            { label: "Feuille de route", code: "roadmap" },
            { label: "Satellite", code: "satellite" },
            { label: "Terrain", code: "terrain" },
            { label: "Hybride", code: "hybrid" }
        ]
    }

    //***********************************************************************************
    //**** transaction refresh
    //***********************************************************************************
    transaction_refresh() {
        super.transaction_refresh();
        this._terminate_edition();
    }

    _terminate_edition() {
        super._terminate_edition();
        this.set_edited_map(null);
    }

    open_tool() {
        super.open_tool();
        this.push_instruction_input(HELPER.background_map.empty_state);
    }

    _get_storey_field() {
        if (this._map._scene.constructor == cn_roof) return 'roof_background_maps';
        return 'background_maps';

    }
    _get_bg_maps() {
        if (this._scene.constructor == cn_roof)
            return this._map._storey.roof_background_maps;
        return this._map._storey.background_maps;
    }

    _insert_bg_map(bm) {
        if (this._map._scene.constructor == cn_roof) {
            this.push_item_set(this._map._storey, 'roof_background_maps');
            this._map._storey.roof_background_maps.push(bm);
        }
        else {
            this.push_item_set(this._map._storey, 'background_maps');
            this._map._storey.background_maps.push(bm);
        }
    }

    _all_bg_maps() {
        return this._building.storeys.flatMap(it => it.background_maps).concat(this._building.storeys.flatMap(it => it.roof_background_maps));
    }

    //***********************************************************************************
    /**
     * sets edited map
     * @param {cn_background_map} map
     */
    set_edited_map(map) {
        if (map == this._edited_map) return;
        this._edited_map = map;
        this._rotation_handle = null;
        this._selected_vector_element = null;
        if (this._edited_map == null) return;

        const obj = this;
        function set_map_orientation(bg_map, value) {
            obj.push_item_set(bg_map, 'orientation');
            bg_map.orientation = value;
            if (bg_map.fixed_map) {
                obj.push_item_set(obj._building, 'compass_orientation');
                obj._building.compass_orientation = -value;
            }
        }

        this._rotation_handle = new cn_handler_rotation(this._edited_map.offset, this._edited_map.orientation, 1, 0.55 * cn_size(this._edited_map.get_world_size()), true);
        this._rotation_handle.on('change', () => {
            this.push_transaction('Rotation du fond de carte', '' + this._grabid + this._edited_map.ID);
            set_map_orientation(this._edited_map, this._rotation_handle.angle);
            this._map.refresh_background_and_overlay();
            this._map.refresh_tool();
            this.call('orientation_change', this._edited_map.orientation);
        });
        this._rotation_handle._angle_callback = () => {
            const input = new cn_number_input('Rotation du fond de carte', this._edited_map.orientation);
            input.min_value = -180;
            input.max_value = 180;
            input.decimals = 1;
            input.unit = '°';
            while (input.value > 180) input.value -= 360;
            while (input.value < -180) input.value += 360;
            const all_background_maps = this._all_bg_maps();
            if (all_background_maps.length > 1) {
                input.checkbox_label = 'Appliquer à toutes les images';
                input.checked_hides_value = false;
            }
            input.callback = () => {
                this.push_transaction('Rotation du fond de carte');
                if (input.checkbox_status) {
                    all_background_maps.forEach(it => set_map_orientation(it, input.value));
                } else {
                    set_map_orientation(this._edited_map, input.value);
                }
                this._map.refresh_background_and_overlay();
                this._map.refresh_tool();
                this.call('orientation_change', this._edited_map.orientation);
            };
            this.call('number_input', input);
        };

        //*** Edit box */
        this._edit_box = new cn_edit_box(this, [map], this._map.get_readonly());

        //*** Layers button button */
        if (this._edited_map.vector_data && this._edited_map.vector_data.layers.length > 1) {
            const layers_pastille = new cn_pastille([0, 0], 'tool_filters.png');
            this._edit_box.add_pastille(layers_pastille);
            layers_pastille.svg_class = 'pastille_background white';
            layers_pastille.title = 'Calques';
            layers_pastille.clicked = () => {
                const layers = this._edited_map.vector_data.layers.map(layer => {
                    return new cn_check_input(`${layer.label} (${layer.elements.length})`, layer.visible);
                });
                const input = new cn_list_input('Calques', layers);
                input.callback = () => {
                    this.push_transaction('Calques du fond de carte');
                    this._edited_map.vector_data.layers.forEach((layer, i) => {
                        this.push_item_set(layer, 'visible');
                        layer.visible = input.inputs[i].value;
                    });
                    this._map.refresh();
                };

                //*** Refresh callback : set visibility then unset after refresh */
                input.refresh_callback = () => {
                    this.push_transaction('Calques du fond de carte');
                    const values = [];
                    this._edited_map.vector_data.layers.forEach((layer, i) => {
                        values.push(layer.visible);
                        layer.visible = input.inputs[i].value;
                    });
                    this._map.refresh();
                    this._edited_map.vector_data.layers.forEach((layer, i) => {
                        layer.visible = values[i];
                    });
                };
                this._map.call('list_input', input);
            };
        }
        
        const layers_colors_pastille = new cn_pastille([0, 0], 'palette.png');
        this._edit_box.add_pastille(layers_colors_pastille);
        layers_colors_pastille.svg_class = 'pastille_background white';
        layers_colors_pastille.title = 'Gestion des couleurs';
        layers_colors_pastille.active = this._edited_map.vector_data? this._edited_map.vector_data.draw_with_colors > 0 : this._edited_map.draw_with_colors > 0;
        layers_colors_pastille.clicked = () => {
            this.push_transaction('Couleurs du fond de carte');
            if (this._edited_map.vector_data) {
                this.push_item_set(this._edited_map.vector_data, 'draw_with_colors');
                this._edited_map.vector_data.draw_with_colors = ((this._edited_map.vector_data.draw_with_colors+1)%3);
                layers_colors_pastille.active = this._edited_map.vector_data.draw_with_colors > 0;
            }
            else {
                this.push_item_set(this._edited_map, 'draw_with_colors');
                this._edited_map.draw_with_colors = ((this._edited_map.draw_with_colors+1)%4);
                layers_colors_pastille.active = this._edited_map.draw_with_colors > 0;
            }
            this.refresh();
        }

        if (this._edited_map.vector_data) {
            const vector_data_label_pastille = new cn_pastille([0, 0], 'toggle_text.png');
            vector_data_label_pastille.active = this._edited_map.vector_data.show_text;
            this._edit_box.add_pastille(vector_data_label_pastille);
            vector_data_label_pastille.svg_class = 'pastille_background white';
            vector_data_label_pastille.title = 'Textes';
            vector_data_label_pastille.clicked = () => {
                this.push_transaction('Affichage des textes du DXF/DWG');
                this.push_item_set(this._edited_map.vector_data, 'show_text');
                this._edited_map.vector_data.show_text = !this._edited_map.vector_data.show_text;
                vector_data_label_pastille.active = this._edited_map.vector_data.show_text;
                this.refresh();
            };
            
            if (this._building.storeys.length > 1)
            {
                const vector_data_duplicate_pastille = new cn_pastille([0, 0], 'tool_copy.png');
                this._edit_box.add_pastille(vector_data_duplicate_pastille);
                vector_data_duplicate_pastille.svg_class = 'pastille_background white';
                vector_data_duplicate_pastille.title = 'Répéter sur les étages';
                vector_data_duplicate_pastille.clicked = () => {
                    const storeys = this._building.storeys.filter(st => {
                        return !st.background_maps.find(bgm => bgm.vector_data && bgm.vector_data.ID == (this._edited_map.vector_data.ID));
                    });

                    if (storeys.length ==0) 
                    {
                        this._map.call('notification_input', new cn_notification_input('Tous les étages contiennent déjà cette carte.'));
                        return;
                    }
                    
                    let storey_checks = storeys.map(st => {
                        return new cn_check_input(`${st.short_name}`, false);
                    });
                    const input = new cn_list_input('Etages', storey_checks);
                    input.callback = () => {
                        if (!storey_checks.find(ch => ch.value)) return;
                        this.push_transaction('Duplication du fond de carte');
                        storey_checks.forEach((ch,i) => {
                            if (ch.value) {
                                let jsonbg = this._edited_map.serialize();
                                jsonbg.ID = cn_element.generate_ID();
                                let bg =cn_background_map.unserialize(jsonbg,this._building);
                                if (bg) {
                                    this.push_item_set(storeys[i],"background_maps");
                                    storeys[i].background_maps.push(bg);
                                }
                            }
                        });
                        this._map.refresh();
                    };
                    input.refresh_callback = () => {};

                    this._map.call('list_input', input);
                };
            }
        }

        //*** Opacity button */
        const opacity_pastille = new cn_pastille([0, 0], 'opacity.svg');
        this._edit_box.add_pastille(opacity_pastille);
        opacity_pastille.svg_class = 'pastille_background white';
        opacity_pastille.title = 'Opacité';
        opacity_pastille.clicked = () => {
            const input = new cn_number_input('Opacité', this._edited_map.background_opacity * 100, '%', 0, 0, 100);
            input.callback = () => {
                this.push_transaction('Opacité du fond de carte', this._edited_map.ID);
                this.push_item_set(this._edited_map, 'background_opacity');
                this._edited_map.background_opacity = input.value / 100;
                this._map.refresh();
            };
            input.slider = true;
            this._map.call('number_input', input);
        };

        const front_pastille = new cn_pastille([0, 0], 'arrange_forward.png');
        this._edit_box.add_pastille(front_pastille);
        front_pastille.svg_class = 'pastille_background white';
        front_pastille.title = 'Avancer';
        front_pastille.visible = this._get_bg_maps().indexOf(this._edited_map) < this._get_bg_maps().length - 1;
        front_pastille.clicked = () => {
            this.change_map_z_position(this._edited_map, true);
            const index = this._get_bg_maps().indexOf(this._edited_map)
            front_pastille.visible = index < this._get_bg_maps().length - 1;
            back_pastille.visible = index > 0;
            this._map.refresh();
        };

        const back_pastille = new cn_pastille([0, 0], 'arrange_backward.png');
        this._edit_box.add_pastille(back_pastille);
        back_pastille.svg_class = 'pastille_background white';
        back_pastille.title = 'Reculer';
        back_pastille.visible = this._get_bg_maps().indexOf(this._edited_map) > 0;
        back_pastille.clicked = () => {
            this.change_map_z_position(this._edited_map, false);
            const index = this._get_bg_maps().indexOf(this._edited_map)
            front_pastille.visible = index < this._get_bg_maps().length - 1;
            back_pastille.visible = index > 0;
            this._map.refresh();
        };

        //*** Copy settings button */
        // -> displayed only if there are others background maps used in building (unused background maps are not taken into account)
        const other_maps = this._building.maps.filter(it => it.ID != this._edited_map.ID && this._building.is_used_background_map(it));
        if (!this._edited_map.fixed_map) {
            if (other_maps.length && !this._map.get_readonly()) {
                const copy_settings_pastille = new cn_pastille([0, 0], 'cog_sync.svg');
                this._edit_box.add_pastille(copy_settings_pastille);
                copy_settings_pastille.svg_class = 'pastille_background white';
                copy_settings_pastille.title = 'Reprendre la mise en forme d\'un autre fond de carte';
                copy_settings_pastille.clicked = () => {

                    const input = new cn_background_map_selection_input('Sélection du fond de carte de référence', other_maps);
                    input.content = 'Sélectionnez le fond de carte dont vous souhaitez reprendre la mise en forme';
                    input.callback = () => {
                        if (input.selection) {
                            this.push_transaction('Mise en forme fond de carte', this._edited_map.ID);
                            this.push_item_set(this._edited_map, 'orientation');
                            this.push_item_set(this._edited_map, 'scale');
                            this.push_item_set(this._edited_map, 'offset');
                            this._edited_map.orientation = input.selection.orientation;
                            this._edited_map.scale = input.selection.scale;
                            this._edited_map.offset = input.selection.offset;
                            this._map.refresh();
                        }
                    };

                    const others_maps_orientations = new Set(other_maps.map(it => it.orientation));
                    const others_maps_scales = new Set(other_maps.map(it => it.scale));
                    const others_maps_offsets = new Set(other_maps.map(it => it.offset));
                    if (others_maps_orientations.size == 1 && others_maps_scales.size == 1 && others_maps_offsets.size == 1) {
                        // if only one other background map setting, apply it with user confirmation
                        const inputConfirm = new cn_question_input(other_maps.length > 1 ? 'Voulez-vous reprendre la mise en forme des autres fonds de carte' : 'Voulez-vous reprendre la mise en forme de l\'autre fond de carte');
                        inputConfirm.severity = SEVERITY_QUESTION_WARNING;
                        inputConfirm.callback = () => {
                            input.selection = other_maps[0];
                            input.callback();
                        }
                        this._map.call('question_input', inputConfirm);
                    } else {
                        this._map.call('background_map_selection_input', input);
                    }
                };
            }
        }

        this._map.refresh();
    }

    _update_fixed_map(map) {
        const progress_input = new cn_progress_input("Chargement de la carte...");
        this._map.call('progress_input', progress_input);
        map.update_image_id(() => {
            this._map.refresh_background_and_overlay();
            this._map.refresh_tool();
            if (progress_input.callback) progress_input.callback(false);
            this.call('selection_change');
        }, progress_input.callback);
    }

    /**
     * returns edited map
     * @return {cn_background_map}
     */
    get_edited_map() {
        return this._edited_map;
    }

    /**
     * Build a new background map
     * @param {string} image_id
     * @param {number} width
     * @param {number} height
     * @return {cn_background_map}
     */
    new_background_map(image_id, width, height) {
        this.push_transaction('Nouveau fond de carte');
        this.push_item_set(this._map._building, 'maps');
        const background_map = this._map._building.new_background_map(image_id, width, height);

        this._insert_bg_map(background_map);
        this.set_edited_map(background_map);
        this._map.refresh();
        return background_map;
    }

    /**
     * Build a new vector background map
     * @param {string} file_contents
     * @return {cn_background_map}
     */
    new_background_map_dxf(file_contents) {
        try {
            this.push_transaction('Nouveau fond de carte');
            this.push_item_set(this._map._building, 'maps');
            const json = cn_dxf_to_json(file_contents);
            return this.new_background_vector_map(json);
        }
        catch (err) {
            console.error(err);
        }
        return null;
    }

    /**
     * Build a new vector background map
     * @param {any} json_vector_data
     * @return {cn_background_map}
     */
    new_background_vector_map(json_vector_data) {

        const background_map = this._map._building.new_background_map("", 100, 100);
        background_map.scale = 1;
        background_map.fixed_map = false;
        background_map.vector_data = json_vector_data;
        json_vector_data.parent = background_map;
        /*new cn_vector_data(background_map);
        if (json_vector_data.layers) {
            const json_layers = json_vector_data.layers;
            for (var key in json_layers) {
                if (json_layers[key].lines.length +
                    json_layers[key].arcs.length +
                    json_layers[key].ellipses.length > 0) {
                    const layer = new cn_vector_layer(background_map.vector_data);
                    layer.label = key;
                    layer.color = json_layers[key].color;
                    layer.visible = true;
                    json_layers[key].lines.forEach(line => {
                        const e = new cn_vector_path(layer);
                        e.closed_contour = line.closedContour;
                        e.vertices = line.vertices.map(jv => [jv.x, jv.y]);
                        layer.elements.push(e);
                    });
                    json_layers[key].arcs.forEach(arc => {
                        const e = new cn_vector_arc(layer);
                        e.center = [arc.x, arc.y];
                        e.radius[0] = arc.radius;
                        e.radius[1] = arc.radius;
                        e.start_angle = arc.startAngle;
                        e.end_angle = arc.endAngle;
                        layer.elements.push(e);
                    });
                    json_layers[key].ellipses.forEach(ellipse => {
                        const e = new cn_vector_arc(layer);
                        e.center = [ellipse.x, ellipse.y];
                        const dx = cn_sub([ellipse.majorAxisEndPoint.x, ellipse.majorAxisEndPoint.y], e.center);
                        e.radius[0] = cn_normalize(dx);
                        e.radius[1] = e.radius[0] * ellipse.axisRatio;
                        e.angle = cn_polar(dx)[1];
                        e.start_angle = ellipse.startAngle;
                        e.end_angle = ellipse.endAngle;
                        layer.elements.push(e);
                    });
                    background_map.vector_data.layers.push(layer);
                }
            }
        }
        if (json_vector_data.texts) {
            background_map.vector_data.texts = json_vector_data.texts.map(text => {
                const result = new cn_vector_text(background_map.vector_data)
                result.position = text.position;
                result.text = text.text;
                result.align = text.align;
                result.rotation = text.rotation
                result.height = text.height
                return result;
            })
        }*/
        let vector_data = background_map.vector_data;
        vector_data.filter_layers();
        vector_data.get_bounding_box();
        let full_size = vector_data.box.size[0] + vector_data.box.size[1];
        if (full_size <= 0) return null;

        background_map.scale = 1;
        while (full_size > 500) {
            background_map.scale /= 10;
            full_size /= 10;
        }
        while (full_size < 15) {
            background_map.scale *= 10;
            full_size *= 10;
        }
        console.log(`applied automatic scale ${background_map.scale}`);
        background_map.initialize_vector_data()

        this._insert_bg_map(background_map);
        this.set_edited_map(background_map);
        this._map.refresh();
        this.call('selection_change');
        return background_map;
    }

    /**
     * Build a new google map
     * @param {number} latitude
     * @param {number} longitude
     * @param {string} maptype
     * @param {function} done_method
     */
    new_google_map(latitude, longitude, maptype, done_method = null) {
        const progress_input = new cn_progress_input("Chargement de la carte...");
        this._map.call('progress_input', progress_input);

        this.push_transaction('Nouveau fond de carte');
        this.push_item_set(this._map._building, 'maps');
        const background_map = this._map._building.new_background_map("", 100, 100);
        background_map.scale = 1;
        background_map.fixed_map = true;
        background_map.longitude = longitude;
        background_map.latitude = latitude;
        background_map.map_type = (this.maptypes.map(mp => mp.code).includes(maptype)) ? maptype : this.maptypes[0].code;
        background_map.update_image_id(() => {
            this._insert_bg_map(background_map);
            this.set_edited_map(background_map);
            this._map.refresh();
            if (done_method) done_method(background_map);
            if (progress_input.callback) progress_input.callback(false);
            this.call('selection_change');
        }, progress_input.callback);
    }

    edit_google_map(latitude, longitude, maptype) {
        const progress_input = new cn_progress_input("Chargement de la carte...");
        this._map.call('progress_input', progress_input);
        const background_map = this.get_current_google_map_for_storey()
        this.push_transaction('Édition du fond de carte');
        this.push_item_set(background_map, ['latitude', 'longitude', 'map_type']);
        background_map.longitude = longitude;
        background_map.latitude = latitude;
        background_map.map_type = (this.maptypes.map(mp => mp.code).includes(maptype)) ? maptype : this.maptypes[0].code;
        background_map.update_image_id(() => {
            this.set_edited_map(background_map);
            this._map.refresh();
            if (progress_input.callback) progress_input.callback(false);
            this.call('selection_change');
        }, progress_input.callback);

    }

    /**
     * Returns the list of all building maps that are not on this storey
     *
     * @return {cn_background_map[]}
     */
    get_other_maps() {
        const storey_maps = this._get_bg_maps();
        const others = [];
        this._map._building.maps.forEach(it => {
            if (storey_maps.find(bm => bm.ID === it.ID) === undefined) {
                others.push(it);
            }
        })
        return others;
    }

    get_current_google_map_for_storey() {
        return this._get_bg_maps().find(bm => bm.fixed_map && bm.latitude !== undefined && bm.longitude !== undefined);
    }

    /**
     * Adds an existing background map to the current storey
     * @param {cn_background_map} map
     * @returns
     */
    insert_background_map(map) {
        if (this._get_bg_maps().indexOf(map) >= 0) return;

        this.push_transaction('Ajout de fond de carte');
        this._insert_bg_map(map);

        this.set_edited_map(map);
        this._map.refresh();
    }

    /**
     * Order map in storey z position (true = front, false = back)
     *
     * @param {cn_background_map} map
     * @param {boolean} front
     */
    change_map_z_position(map, front = true) {
        const index = this._get_bg_maps().indexOf(map);
        if (index >= 0) {
            this.push_transaction((front ? 'Avancer' : 'Reculer') + ' fond de carte');
            this.push_item_set(this._map._storey, this._get_storey_field());
            this._get_bg_maps().splice(index, 1);
            if (front) {
                this._get_bg_maps().splice(index + 1, 0, map);
            } else {
                this._get_bg_maps().splice(index - 1, 0, map);
            }
        }
    }

    //***********************************************************************************
    /**
     * Open tool
     */
    // open_tool() {
    // }

    draw(camera) {
        var html = '';

        //*** Draw contour of mouseover map */
        if (this._mouseover_map) {
            var pos = camera.world_to_screen(this._mouseover_map.offset);
            var sz = cn_mul(this._mouseover_map.get_world_size(), camera.world_to_screen_scale);
            html += `<rect class="map_contour_mouseover" x="${-sz[0] * 0.5}" y="${-sz[1] * 0.5}" width="${sz[0]}" height="${sz[1]}" transform="translate(${pos[0]},${pos[1]}) rotate(${-this._mouseover_map.orientation})" />`;
        }

        this._update_move_handles(camera);
        if (this._edited_map) {

            //*** draw rotation handle */
            if (this._rotation_handle) {
                this._rotation_handle.center = cn_clone(this._edited_map.offset);
                this._rotation_handle.angle = this._edited_map.orientation;
                this._rotation_handle.world_radius = 0.55 * cn_size(this._edited_map.get_world_size());

                html += this._rotation_handle.draw(camera);
            }

            //*** Draw contour of edtied map */
            var pos = camera.world_to_screen(this._edited_map.offset);
            var sz = cn_mul(this._edited_map.get_world_size(), camera.world_to_screen_scale);
            html += `<rect class="map_contour_selected" x = "${-sz[0] * 0.5}" y = "${-sz[1] * 0.5}" width = "${sz[0]}" height = "${sz[1]}" transform = "translate(${pos[0]},${pos[1]}) rotate(${-this._edited_map.orientation})" /> `;

            //*** Draw reference line
            if (!this._edited_map.fixed_map && !this._edited_map.vector_data) {
                var rp0 = this._edited_map.to_world(this._edited_map.reference_points[0]);
                var rp1 = this._edited_map.to_world(this._edited_map.reference_points[1]);
                var p0 = camera.world_to_screen(rp0);
                var p1 = camera.world_to_screen(rp1);


                let xtraclass = (this._mouseover == 2) ? ' line_selected mouseover' : (this._selection == 2) ? ' line_selected selected' : '';
                html += '<line class=\'map_reference_line' + xtraclass + '\' x1=\'' + p0[0] + '\' y1=\'' + p0[1] + '\' x2=\'' + p1[0] + '\' y2=\'' + p1[1] + '\' />';
                xtraclass = (this._mouseover == 0) ? 'mouseover' : (this._selection == 0) ? 'selected' : '';
                html += camera.draw_move_arrow(rp0, xtraclass);
                xtraclass = (this._mouseover == 1) ? 'mouseover' : (this._selection == 1) ? 'selected' : '';
                html += camera.draw_move_arrow(rp1, xtraclass);

                if (this._mouseover == 2) html += camera.draw_line_move_arrow(rp0, rp1);
                html += camera.draw_force_measure(rp0, rp1, this._scale_measure, this._mouseover == 6, 0);
            }

            const p00 = this._edited_map.to_world([0, 0]);
            const p10 = this._edited_map.to_world([1, 0]);
            const p01 = this._edited_map.to_world([0, 1]);
            const p11 = this._edited_map.to_world([1, 1]);


            if (this._mouseover == 7) html += camera.draw_line_move_arrow(p00, p10);
            if (this._mouseover == 8) html += camera.draw_line_move_arrow(p10, p11);
            if (this._mouseover == 9) html += camera.draw_line_move_arrow(p11, p01);
            if (this._mouseover == 10) html += camera.draw_line_move_arrow(p01, p00);

            html += camera.draw_force_measure(p00, p10, this._width_measure, this._mouseover == 4);
            html += camera.draw_force_measure(p01, p00, this._height_measure, this._mouseover == 5);

            //*** Draw move handles
            this._move_handles.forEach(mh => {
                mh.position = this._edited_map.to_world(mh['map_position']);
                html += mh.draw(camera);
            });

            //*** crop handles */
            const cp00 = camera.world_to_screen(this._edited_map.to_world(this._edited_map.crop_min));
            const cp10 = camera.world_to_screen(this._edited_map.to_world([this._edited_map.crop_max[0], this._edited_map.crop_min[1]]));
            const cp01 = camera.world_to_screen(this._edited_map.to_world([this._edited_map.crop_min[0], this._edited_map.crop_max[1]]));
            const cp11 = camera.world_to_screen(this._edited_map.to_world(this._edited_map.crop_max));
            let dx = cn_sub(cp10,cp00);
            cn_normalize(dx);
            dx = cn_mul(dx,20);
            let dy = cn_sub(cp01,cp00);
            cn_normalize(dy);
            dy = cn_mul(dy,20);

            html += camera.draw_path_screen([cn_add(cp00,dy),cp00,cn_add(cp00,dx)],this._mouseover === 14?"trim-handle mouseover":"trim-handle");
            html += camera.draw_path_screen([cn_sub(cp11,dy),cp11,cn_sub(cp11,dx)],this._mouseover === 15?"trim-handle mouseover":"trim-handle");
            /*html += `<path d="M ${cp00[0] + dy[0]} ${cp00[0] + dy[0]} h -${dist} v -${dist}" class="trim-handle ${this._mouseover === 14 ? 'mouseover' : ''}" />`
            html += `<path d="M ${max[0] - dist} ${max[1]} h ${dist} v ${dist}" class="trim-handle ${this._mouseover === 15 ? 'mouseover' : ''}" />`

            if (this._edited_map.vector_data) {
                const min = camera.world_to_screen(cn_mul(this._edited_map.vector_data.min, this._edited_map.scale));
                const max = camera.world_to_screen(cn_mul(this._edited_map.vector_data.max, this._edited_map.scale));
                const p1 = camera.world_to_screen(cn_mul(cn_add(this._edited_map.vector_data_position, cn_mul(this._edited_map.image_size, 0.5)), this._edited_map.scale));
                const p0 = camera.world_to_screen(this._edited_map.offset);
                const dist = 20
                html += `<g transform="translate(${p0[0]},${p0[1]}) rotate(${-this._edited_map.orientation}) translate(${-p1[0]},${-p1[1]})">`;
                // html += `<line x1="${min[0]}" y1="${min[1]}" x2="${min[0]}" y2="${limit_min[1]}" class="trim-handle-hint" />`
                // html += `<line x1="${min[0]}" y1="${min[1]}" x2="${limit_min[0]}" y2="${min[1]}" class="trim-handle-hint" />`
                html += `<rect x="${min[0]}" y="${max[1]}" width="${max[0]-min[0]}" height="${-max[1]+min[1]}" fill="none" stroke="rgb(100,100,255)" />`
                html += `<path d="M ${min[0] + dist} ${min[1]} h -${dist} v -${dist}" class="trim-handle ${this._mouseover === 14 ? 'mouseover' : ''}" />`
                html += `<path d="M ${max[0] - dist} ${max[1]} h ${dist} v ${dist}" class="trim-handle ${this._mouseover === 15 ? 'mouseover' : ''}" />`
                html += `</g>`;
            }*/

            //*** Draw mouseover vector data
            if (this._mouseover_vector_element && this._mouseover == 13) {
                const sc0 = camera.world_to_screen(this._mouseover_vector_element[0]);
                const sc1 = camera.world_to_screen(this._mouseover_vector_element[1]);
                html += `<line stroke="yellow" stroke-width="5"  x1="${sc0[0]}" y1="${sc0[1]}" x2="${sc1[0]}" y2="${sc1[1]}"  />`;
            }

            //*** Draw selected vector data
            if (this._selected_vector_element) {
                const p0 = this._edited_map.to_world(this._selected_vector_element[0]);
                const p1 = this._edited_map.to_world(this._selected_vector_element[1]);
                const sc0 = camera.world_to_screen(p0);
                const sc1 = camera.world_to_screen(p1);
                html += `<line stroke="red" stroke-width="5"  x1="${sc0[0]}" y1="${sc0[1]}" x2="${sc1[0]}" y2="${sc1[1]}"  />`;
                html += camera.draw_force_measure(p0, p1, this._vector_measure, this._mouseover == 12);
            }

            //*** draw edito box */
            html += this._edit_box.draw(camera);
        }

        return html;
    }

    //***********************************************************************************
    //**** Update offset buttons
    //***********************************************************************************
    _update_move_handles(camera) {
        if (this._edited_map == null) {
            this._move_handles = [];
            this._current_camera_scale = 0;
            return;
        }

        //*** camera scale didn't change ? */
        if (this._move_handles.length > 0 && this._current_camera_scale == camera.world_to_screen_scale) return;

        this._current_camera_scale = camera.world_to_screen_scale;

        var image_size = this._edited_map.get_world_size();
        var canvas_size = camera.get_world_size();
        var nx = 1 + Math.floor(2 * image_size[0] / canvas_size[0]);
        if (nx < 1) nx = 1;
        var ny = 1 + Math.floor(2 * image_size[1] / canvas_size[1]);
        if (ny < 1) ny = 1;

        var dx = 1 / nx;
        var dy = 1 / ny;

        this._move_handles = [];
        for (var i = 0; i < nx; i++) {
            var x = dx * (0.5 + i);
            for (var j = 0; j < ny; j++) {
                var y = dy * (0.5 + j);
                const proj = camera.world_to_screen(this._edited_map.to_world([x, y]));
                if (!proj || proj[0] < 0 || proj[1] < 0 || proj[0] > camera._width || proj[1] > camera._height) continue;
                var h = new cn_pastille([0, 0], 'arrow_all_white.svg');
                h['map_position'] = [x, y];
                this._move_handles.push(h);
            }
        }
    }

    //*****************************************************************
    //*** Clear move data
    //*****************************************************************
    clear_move() {
        this._mouseover = -1;
        this._mouseover_map = null;
        for (var k = 0; k < this._move_handles.length; k++) {
            this._move_handles[k].mouseover = false;
        }
        if (this._rotation_handle) this._rotation_handle.clear_move();
        if (this._edit_box) this._edit_box.clear_move();
        this._mouseover_vector_element = null;
    }

    //***********************************************************************************
    /**
     * click management
     */
    click(ev) {
        if (this._edit_box && this._edit_box.click(ev))
            return true;

        if (this._rotation_handle && this._rotation_handle.click(ev))
            return true;

        if (this._selection >= 4 && this._selection <= 6 && this._edited_map) {
            const input = new cn_number_input();
            const world_size = this._edited_map.get_world_size();
            if (this._selection == 4) {
                input.label = 'Largeur de la carte';
                input.value = world_size[0];
            } else if (this._selection == 5) {
                input.label = 'Longueur de la carte';
                input.value = world_size[1];
            } else {
                input.label = 'Longueur de la référence';
                const rp0 = this._edited_map.to_world(this._edited_map.reference_points[0]);
                const rp1 = this._edited_map.to_world(this._edited_map.reference_points[1]);
                input.value = cn_dist(rp0, rp1);
            }
            const initial_value = input.value;
            input.unit = 'm';
            input.decimals = 2;
            input.min_value = 0.1;
            input.max_value = 10000;
            const all_background_maps = this._all_bg_maps();
            if (all_background_maps.length > 1) {
                input.checkbox_label = 'Appliquer l\'échelle à toutes les images';
                input.checked_hides_value = false;
            }
            input.callback = () => {
                if (this._edited_map.fixed_map) {
                    const size_index = (this._selection == 4) ? 0 : 1;
                    if (input.value > 1 && this._edited_map.image_size[size_index] != input.value) {
                        this.push_transaction('Dimensions du fond de carte');
                        this.push_item_set(this._edited_map, 'image_size', () => this._edited_map.update_image_id());
                        this._edited_map.image_size[size_index] = input.value;
                        this._update_fixed_map(this._edited_map)
                    }
                }
                else {
                    const coef = input.value / initial_value;
                    if (world_size[0] * coef > 0.01 && world_size[1] * coef > 0.01) {
                        const new_scale = this._edited_map.scale * coef;
                        this.push_transaction('Dimensions du fond de carte');
                        if (input.checkbox_status) {
                            all_background_maps.forEach(it => {
                                this.push_item_set(it, 'scale');
                                it.scale = new_scale;
                            });
                        } else {
                            this.push_item_set(this._edited_map, 'scale');
                            this._edited_map.scale = new_scale;
                        }
                        this._current_camera_scale = -1;
                        this._map.refresh_background_and_overlay();
                        this._map.refresh_tool();
                    }
                }
            }

            this.call('number_input', input);
        }

        //*** Maybe we select a vector element */
        if (this._selection == 13 && this._mouseover_vector_element) {
            this._selected_vector_element = this._mouseover_vector_element.map(v => this._edited_map.to_image(v));
        }

        //*** Maybe we click on a vector element measure */
        if (this._selection == 12 && this._selected_vector_element) {
            const input = new cn_number_input();
            input.label = 'Longueur de l\'élément';
            const rp0 = this._edited_map.to_world(this._selected_vector_element[0]);
            const rp1 = this._edited_map.to_world(this._selected_vector_element[1]);
            const initial_value = cn_dist(rp0, rp1);
            input.value = initial_value;
            input.unit = 'm';
            input.decimals = 2;
            input.min_value = 0.1;
            input.max_value = 10000;

            input.callback = () => {
                if (input.value > 0 && initial_value != input.value) {
                    this.push_transaction('Dimensions du fond de carte');
                    this.push_item_set(this._edited_map, 'scale');
                    this._edited_map.scale *= input.value / initial_value;
                    this._map.refresh_background_and_overlay();
                    this._map.refresh_tool();
                }
            }

            this.call('number_input', input);
        }
        return true;
    }

    //***********************************************************************************
    /**
     * passive move management
     */
    move(ev) {
        this._manage_pending_changes();
        this.clear_move();
        if (this._edit_box && this._edit_box.move(ev))
            return true;

        this._mouseover = this._find_mouseover(ev)
        if (this._mouseover < 0 && this._rotation_handle) this._rotation_handle.move(ev);
        return true;
    }

    //***********************************************************************************
    /**
     * Grab
     */
    grab(ev) {
        if (this._edit_box && this._edit_box.grab(ev))
            return true;

        this._grabid++;
        this._selection = -1;
        this.clear_move();
        this._mouseover = this._find_mouseover(ev);
        if (this._mouseover_map) {
            this.set_edited_map(this._mouseover_map);
            this.call('selection_change');
            return true;
        } else if (this._mouseover >= 0) {
            this._mouse_position = cn_clone(ev.mouse_world);
            this._selection = this._mouseover;
            if (this._selection == 11 && this._rotation_handle)
                this._rotation_handle.grab(ev);

            return true;
        } else
            return false;
    }

    drop(ev) {
        this._manage_pending_changes();
        if (this._edit_box && this._edit_box.drop(ev))
            return true;

        if (this._selection >= 0) {
            if (this._selection == 11 && this._rotation_handle)
                return this._rotation_handle.drop(ev);
            return true;
        }

        return false;
    }

    drag(ev) {
        if (this._edit_box && this._edit_box.drag(ev))
            return true;

        if (this._selection >= 0 && this._edited_map) {
            var offset = cn_sub(ev.mouse_world, this._mouse_position);

            //*** Move the map */
            if (this._selection == 3) {
                this.push_transaction('Déplacement du fond de carte', this._edited_map.ID + this._grabid);
                this.push_item_set(this._edited_map, 'offset');
                this._edited_map.offset = cn_add(this._edited_map.offset, offset);
                this._edited_map.old_offset = cn_clone(this._edited_map.offset);
                this._map.refresh_background_and_overlay();
            }
            //*** move some of the reference points */
            else if (this._selection <= 2) {
                this.push_transaction('Déplacement des points de référence', this._edited_map.ID + this._grabid);
                this.push_item_set(this._edited_map, 'reference_points');
                var p0 = this._edited_map.to_world(this._edited_map.reference_points[0]);
                var p1 = this._edited_map.to_world(this._edited_map.reference_points[1]);
                if (this._selection != 1) p0 = cn_add(p0, offset);
                if (this._selection != 0) p1 = cn_add(p1, offset);
                this._edited_map.reference_points[0] = this._edited_map.to_image(p0);
                this._edited_map.reference_points[1] = this._edited_map.to_image(p1);
            }
            //*** resize a fixed map */
            else if (this._edited_map.fixed_map && (this._selection === 14 || this._selection === 15)) {
                var coefs = [1,1];
                var offset = [0, 0]
                const img = this._edited_map.to_image(ev.mouse_world);
                if (this._selection == 14) {
                    coefs[1] = 1 - img[1];
                    coefs[0] = 1-img[0];
                    offset = this._edited_map.to_world([0.5 + 0.5 * img[0], 0.5 + 0.5 * img[1]]);
                } else {
                    coefs[1] = img[1];
                    coefs[0] = img[0];
                    offset = this._edited_map.to_world([0.5 + 0.5 * (img[0] - 1), 0.5 + 0.5 * (img[1] - 1)]);
                }
                coefs[0] *= this._edited_map.scale;
                coefs[1] *= this._edited_map.scale;
                if (coefs[0] * this._edited_map.image_size[0] > 0.01 && coefs[1] * this._edited_map.image_size[1] > 0.01) {
                    this.push_transaction('Dimensions du fond de carte', this._edited_map.ID + this._grabid);
                    this.push_item_set(this._edited_map, ['image_size', 'position', 'longitude', 'latitude']);
                    const old_center = this._edited_map.to_world([0.5, 0.5]);
                    this._edited_map.offset = offset;
                    this._edited_map.image_size[0] *= coefs[0];
                    this._edited_map.image_size[1] *= coefs[1];
                    this._edited_map.update_coordinates(old_center);
                    this._pending_changes = true;
                }
                this._map.refresh_background_and_overlay();
            }
            //*** resize the map */
            else if (this._selection >= 7 && this._selection <= 10 && !this._edited_map.fixed_map) {
                var coef = 1;
                var offset = [0, 0]
                const img = this._edited_map.to_image(ev.mouse_world);
                if (this._selection == 7) {
                    coef = 1 - img[1];
                    offset = this._edited_map.to_world([0.5, 0.5 + 0.5 * img[1]]);
                } else if (this._selection == 8) {
                    coef = img[0];
                    offset = this._edited_map.to_world([0.5 + 0.5 * (img[0] - 1), 0.5]);
                } else if (this._selection == 9) {
                    coef = img[1];
                    offset = this._edited_map.to_world([0.5, 0.5 + 0.5 * (img[1] - 1)]);
                } else if (this._selection == 10) {
                    coef = 1 - img[0];
                    offset = this._edited_map.to_world([0.5 + 0.5 * img[0], 0.5]);
                }
                coef *= this._edited_map.scale;
                if (coef * this._edited_map.image_size[0] > 0.01 && coef * this._edited_map.image_size[1] > 0.01) {
                    this.push_transaction('Dimensions du fond de carte', this._edited_map.ID + this._grabid);
                    if (this._edited_map.fixed_map) {
                        this.push_item_set(this._edited_map, ['image_size', 'position', 'longitude', 'latitude']);
                        const old_center = this._edited_map.to_world([0.5, 0.5]);
                        this._edited_map.offset = offset;
                        this._edited_map.image_size[this._selection & 1] *= coef;
                        this._edited_map.update_coordinates(old_center);
                        this._pending_changes = true;
                    }
                    else {
                        this.push_item_set(this._edited_map, ['scale', 'offset']);
                        this._edited_map.scale = coef;
                        this._edited_map.offset = offset;
                    }
                }
                this._map.refresh_background_and_overlay();
            } else if (this._selection == 11 && this._rotation_handle) {
                return this._rotation_handle.drag(ev);
            } else if (this._selection === 14) {
                this.push_transaction('Rognage du fond de carte', this._edited_map.ID + this._grabid);
                this.push_item_set(this._edited_map, 'crop_min')
                const new_min = this._edited_map.to_image(ev.mouse_world);
                if (new_min[0]<0) new_min[0] = 0;
                else if (new_min[0] > this._edited_map.crop_max[0]) new_min[0] = this._edited_map.crop_max[0];
                if (new_min[1]<0) new_min[1] = 0;
                else if (new_min[1] > this._edited_map.crop_max[1]) new_min[1] = this._edited_map.crop_max[1];
                this._edited_map.crop_min = new_min;
                this._map.refresh_background_and_overlay();
            } else if (this._selection === 15) {
                this.push_transaction('Rognage du fond de carte', this._edited_map.ID + this._grabid);
                this.push_item_set(this._edited_map, 'crop_max')
                const new_max = this._edited_map.to_image(ev.mouse_world);
                if (new_max[0]<this._edited_map.crop_min[0]) new_max[0] = this._edited_map.crop_min[0];
                else if (new_max[0] > 1) new_max[0] = 1;
                if (new_max[1]<this._edited_map.crop_min[1]) new_max[1] = this._edited_map.crop_min[1];
                else if (new_max[1] > 1) new_max[1] = 1;
                this._edited_map.crop_max = new_max;
                this._map.refresh_background_and_overlay();
            }

            this._mouse_position = cn_clone(ev.mouse_world);
            return true;
        }
        return true;
    }

    _manage_pending_changes() {
        if (!this._pending_changes) return;
        if (this._edited_map && this._edited_map.fixed_map)
            this._update_fixed_map(this._edited_map)
        this._pending_changes = false;
    }

    //***********************************************************************************
    //**** Check what is under mouse
    //***********************************************************************************
    _find_mouseover(ev) {

        function mouseover_measure(measure) {
            if (Math.abs(measure[0] - ev.mouse_screen[0]) > 30) return false;
            if (Math.abs(measure[1] - ev.mouse_screen[1]) > 10) return false;
            return true;
        }

        if (this._edited_map) {

            //*** mouse on one of the move handles ? */
            for (var k = 0; k < this._move_handles.length; k++) {
                this._move_handles[k].mouseover = this._move_handles[k].contains(ev.mouse_world, ev.camera);
                if (this._move_handles[k].mouseover) return 3;
            }

            if (ev.camera.on_vertex(ev.mouse_world, this._edited_map.to_world(this._edited_map.crop_min), 20)) return 14
            if (ev.camera.on_vertex(ev.mouse_world, this._edited_map.to_world(this._edited_map.crop_max), 20)) return 15

            //*** Mouse over the reference handles ? */
            if (!this._edited_map.fixed_map && !this._edited_map.vector_data) {
                var p0 = this._edited_map.to_world(this._edited_map.reference_points[0]);
                var p1 = this._edited_map.to_world(this._edited_map.reference_points[1]);
                if (ev.camera.on_vertex(ev.mouse_world, p0)) return 0;
                if (ev.camera.on_vertex(ev.mouse_world, p1)) return 1;
                if (mouseover_measure(this._scale_measure)) return 6;
                if (ev.camera.on_edge(ev.mouse_world, p0, p1)) return 2;
            }

            //*** mouse on one of the size measures ? */
            if (mouseover_measure(this._width_measure)) return 4;
            if (mouseover_measure(this._height_measure)) return 5;

            //*** Mouse over the size handles ? */
            if (!this._edited_map.fixed_map)
            {
                var p00 = this._edited_map.to_world([0, 0]);
                var p10 = this._edited_map.to_world([1, 0]);
                var p01 = this._edited_map.to_world([0, 1]);
                var p11 = this._edited_map.to_world([1, 1]);
                if (ev.camera.on_edge(ev.mouse_world, p00, p10)) return 7;
                if (ev.camera.on_edge(ev.mouse_world, p10, p11)) return 8;
                if (ev.camera.on_edge(ev.mouse_world, p11, p01)) return 9;
                if (ev.camera.on_edge(ev.mouse_world, p01, p00)) return 10;
            }

            //*** Mouse over the rotation handle ? */
            if (this._rotation_handle && this._rotation_handle.move(ev)) return 11;

            //*** Mouse over an element */
            if (this._edited_map.vector_data) {
                if (this._selected_vector_element) {
                    if (mouseover_measure(this._vector_measure)) return 12;
                }

                this._mouseover_vector_element = this._edited_map.find_vector_data_element(ev.mouse_world, ev.camera.snap_world_distance);
                if (this._mouseover_vector_element) return 13;
            }
        }

        if ((this._edited_map == null || !this._edited_map.contains(ev.mouse_world)) &&
            !this._map._storey.draw_previous_storey && !this._map._storey.draw_exterior) {
            const maps = [...this._get_bg_maps()].reverse();
            for (let map of maps) {
                if (map.contains(ev.mouse_world)) {
                    this._mouseover_map = map;
                    break;
                }
            }
        }

        return -1;
    }

}

